import type { ComponentProps } from "react";
import type { ControllerProps, FieldPath } from "react-hook-form";
import { z } from "zod";

import type { Input } from "../../input";

import { PERIOD } from "~/shared/str";

function getFieldSchema<
  Schema extends z.ZodObject<z.ZodRawShape>,
  Path extends FieldPath<z.input<Schema>>,
>(schema: Schema, path: Path) {
  const fieldSchema = _getFieldSchema(schema, path.split(PERIOD));
  if (!fieldSchema) throw new TypeError(`Failed to get field schema for path "${path}"`);

  return fieldSchema;
}

function _getFieldSchema<Schema extends z.ZodSchema>(
  schema: Schema,
  segments: string[],
): z.ZodSchema | null | undefined {
  if (schema instanceof z.ZodDefault) return _getFieldSchema(schema.removeDefault(), segments);
  if (schema instanceof z.ZodNullable || schema instanceof z.ZodOptional)
    return _getFieldSchema(schema.unwrap(), segments);
  if (schema instanceof z.ZodPipeline) return _getFieldSchema(schema._def.in, segments);
  if (schema instanceof z.ZodEffects) return _getFieldSchema(schema.innerType(), segments);

  if (!schema || segments.length === 0) return schema;

  return _getFieldSchema(
    schema instanceof z.ZodObject
      ? (schema.shape as Record<string, unknown>)[segments[0]!]
      : schema instanceof z.ZodArray
        ? schema.element
        : null,
    segments.slice(1),
  );
}

const INFER_ATTRS = {
  input: <Schema extends z.ZodSchema>(
    schema: Schema,
    { field }: Parameters<ControllerProps<z.infer<Schema>>["render"]>[0],
    isValueAcceptable?: (value: number) => boolean,
  ): ComponentProps<typeof Input> => {
    if (schema instanceof z.ZodString)
      return {
        type: "text",
        maxLength: schema.maxLength ?? undefined,
      };

    if (schema instanceof z.ZodNumber) {
      const MULTIPLE_OF = "multipleOf";
      const multipleOf = (
        schema._def.checks.find(({ kind }) => kind === MULTIPLE_OF) as
          | Extract<
              z.ZodNumberCheck,
              {
                kind: typeof MULTIPLE_OF;
              }
            >
          | undefined
      )?.value;

      return {
        type: "number",
        ...(typeof multipleOf === "number" && {
          step: multipleOf,
        }),
        onChange: (event) => {
          const value = event.target.valueAsNumber;
          if (isNaN(value) || (isValueAcceptable && !isValueAcceptable(value))) return;

          field.onChange(value);
        },
      };
    }

    return {};
  },
};

export { getFieldSchema, INFER_ATTRS };
